import React from 'react';
import { makeStyles, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, TableSortLabel, Toolbar, Typography, TextField, Icon, IconButton, Tooltip } from '@material-ui/core';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import { ContextMenu, MenuItem, ContextMenuTrigger } from "react-contextmenu";
import '../react-contextmenu.css';

export default function AccordianTable(props) {
  const classes = useStyles();
  const {
    title,
    size,
    headers,
    rows,
    actions,
    search,
    setSearch,
    defaultOrder,
    defaultOrderBy,
    order,
    orderBy,
    setOrder,
    setOrderBy,
    tablePage,
    setTablePage,
    rowsPerPage,
    rowsPerPageOptions,
    setRowsPerPage,
    setExpandedRowId,
    button,
    className,
    // New props for server-side pagination
    totalCount,
    onPageChange,
    onRowsPerPageChange,
    serverSidePagination,
  } = props;

  let contextTrigger = null;

  const handleSearchInput = event => {
    setSearch && setSearch(event.target.value.toLocaleLowerCase());
  };

  const handleResetTable = () => {
    setSearch && setSearch(``);
    setOrder && setOrder(defaultOrder);
    setOrderBy && setOrderBy(defaultOrderBy);
    setTablePage && setTablePage(0);
    setRowsPerPage && setRowsPerPage(rowsPerPageOptions[0]);
    if (setExpandedRowId) setExpandedRowId(0);
  };

  const handleSortTable = (event, property) => {
    const isAscending = orderBy === property && order === 'asc';
    setOrder && setOrder(isAscending ? 'desc' : 'asc');
    setOrderBy && setOrderBy(property);
  };
  const handleSortByProperty = property => event => {
    handleSortTable(event, property);
  };

  const handleChangePage = (event, newPage) => {
    if (serverSidePagination && onPageChange) {
      onPageChange(newPage);
    }
    setTablePage && setTablePage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (serverSidePagination && onRowsPerPageChange) {
      onRowsPerPageChange(newRowsPerPage);
    }
    setRowsPerPage && setRowsPerPage(newRowsPerPage);
    setTablePage && setTablePage(0)
  }

  const handleOptionsOpen = e => {
    if (contextTrigger) {
      contextTrigger.handleContextClick(e);
    }
  }

  const collect = (props) => {
    return props
  }

  return (
    <>
      <div className={classes.paper + ' ' + className}>
        <Toolbar className={classes.toolbar}>
          <div className={classes.tableName}>
            <div className={classes.name}>
              <Typography className={classes.titleTxt}>{title}</Typography>
            </div>
          </div>

          <div className={classes.tableTools}>
            {setSearch && (
              <div className={classes.search}>
                <TextField
                  fullWidth
                  name='search'
                  label='Filter'
                  placeholder='Filter...'
                  margin='dense'
                  variant='outlined'
                  value={search}
                  onChange={handleSearchInput}
                  className={classes.searchBar}
                />
              </div>
            )}

            <div className={classes.icon}>
              <Tooltip title='Reset Table'>
                <IconButton onClick={handleResetTable}>
                  <Icon>cached</Icon>
                </IconButton>
              </Tooltip>
            </div>

            <div className={classes.icon}>
              <ContextMenuTrigger
                id={`at-options-cm`}
                ref={c => (contextTrigger = c)}
                holdToDisplay={1000}
                collect={collect}
                disableIfShiftIsPressed={true}
              >
                <Tooltip title={`Options`}>
                  <IconButton onClick={handleOptionsOpen}>
                    <Icon>settings</Icon>
                  </IconButton>
                </Tooltip>
              </ContextMenuTrigger>
              {actions && actions.length > 0 && (
                <ContextMenu id={`at-options-cm`} style={{ boxShadow: '2px 4px 12px #00000050' }}>
                  {actions.map(
                    action =>
                      !action.hide && (
                        <MenuItem
                          data={{ action: action, ...action.data }}
                          onClick={action.handler}
                          disabled={action.disabled}
                          key={`atr-table-option-${action.name}`}
                        >
                          {action.label}
                        </MenuItem>
                      )
                  )}
                </ContextMenu>
              )}
            </div>
          </div>
        </Toolbar>

        <div className={classes.tableWrapper}>
          <Table className={classes.table} size={size ? size : `medium`}>
            <TableHead>
              <TableRow>
                {headers.map(
                  header =>
                    !header.hide && (
                      <TableCell
                        key={header.id}
                        align={header.align}
                        style={{ padding: '8px 5px' }}
                        sortDirection={orderBy === header.id ? order : false}
                      >
                        {header.value || (
                          <TableSortLabel
                            className={classes.headTxt}
                            active={orderBy === header.id}
                            direction={order}
                            onClick={handleSortByProperty(header.id)}
                            IconComponent={UnfoldMoreIcon}
                          >
                            <Typography style={{ fontWeight: 'bold', textAlign: 'center', fontSize: '.875rem' }}>
                              {header.label}
                            </Typography>
                            {orderBy === header.id ? (
                              <span className={classes.visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </span>
                            ) : null}
                          </TableSortLabel>
                        )}
                      </TableCell>
                    )
                )}
              </TableRow>
            </TableHead>
            <TableBody>{props.children}</TableBody>
          </Table>
        </div>

        {button}

        <TablePagination
          component='div'
          count={serverSidePagination && totalCount ? totalCount : rows.length}
          page={tablePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className={classes.pagination}
        />
      </div>
    </>
  );
}

const useStyles = makeStyles(theme => ({
  paper: {
    position: 'relative',
    width: '100%',
    border: '1px solid #ddd',
    borderRadius: '8px',
    marginBottom: theme.spacing(2),
    background: '#fff',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    minWidth: '400px',
  },
  toolbar: {
    position: 'relative',
    minHeight: '80px',
    padding: theme.spacing(2),
    borderRadius: '8px 8px 0 0',
    background: '#fff',
    color: theme.palette.text.primary,
  },
  tableName: {
    left: theme.spacing(2),
    position: 'absolute',
    height: '48px',
  },
  name: {
    width: '100%',
    height: '100%',
    paddingRight: theme.spacing(1),
    verticalAlign: 'top',
  },
  titleTxt: {
    margin: '8px 0 0 0',
    fontSize: '20px',
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      margin: '12px 0 0 0',
      fontSize: '16px',
    },
    [theme.breakpoints.down('xs')]: {
      margin: '14px 0 0 0',
      fontSize: '14px',
    },
  },
  tableTools: {
    right: theme.spacing(2),
    position: 'absolute',
    height: '48px',
    [theme.breakpoints.down('sm')]: {
      right: theme.spacing(1.25),
    },
    [theme.breakpoints.down('xs')]: {
      right: theme.spacing(0.5),
    },
  },
  search: {
    display: 'inline-block',
    height: '100%',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    marginRight: '8px',
    verticalAlign: 'top',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  searchBar: {
    width: '320px',
    margin: '4px 0 0 0',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  icon: {
    display: 'inline-block',
    height: '100%',
    verticalAlign: 'top',
  },
  headTxt: {
    fontSize: '12px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '11px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '10px',
    },
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  notFound: {
    padding: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  button1: {
    zIndex: 1001,
    position: 'absolute',
    bottom: theme.spacing(1.5),
    left: theme.spacing(1.5),
    width: '160px',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  button2: {
    position: 'relative',
    width: '100%',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  pagination: {
    borderRadius: '0 0 8px 8px',
    background: '#fff',
    fontSize: '12px',
  },
}))